import { Button, Icon } from 'components/common';
import CoinPurchaseAnimation, { CoinPurchaseAnimationRef } from 'components/promotion/CoinPurchaseAnimation';
import useBottomSheet from 'hooks/common/useBottomSheet';
import { useEffect, useRef, useState } from 'react';

const DAY_MS = 24 * 60 * 60 * 1000;

// 문구 = brsc 리소스(wpass_*). 표기 원칙 = 획득 프레임·손실압박 X·타이머는 진짜 잔여시간만.

interface PassLog {
  startAt: number;
  days?: number;
  daily?: number;
  claimed?: Record<string, number>;
  entitlementId?: string;
}

// 미러 1건을 식별하는 키. 서버가 회수·만료로 거절한 패스를 tombstone 으로 기억할 때 쓴다.
const passKey = (p: PassLog) => p.entitlementId ?? String(p.startAt);

// 서버가 거절한 패스 키. **모듈 스코프**라 시트를 닫고 다시 열어도(컴포넌트 재마운트) 유지된다 —
// 컴포넌트 ref 에 두면 재마운트 시 stale 미러가 죽은 수령 화면을 다시 띄운다.
// 키가 purchaseToken 이라 새로 산 패스는 걸리지 않는다. 페이지 리로드로 초기화(그때는 미러도 새로 읽는다).
const revokedKeys = new Set<string>();

const readPassLog = (): PassLog | null => {
  const p: PassLog | null = (window as any).log?.weeklyPass ?? null;
  // 손상 레코드(startAt/days/daily 결손)는 보유로 보지 않음 — claim(fbutil) 손상 기준과 동일.
  if (!p || !p.startAt || !p.days || !p.daily) return null;
  // 만료 패스(마지막 슬롯 창 종료 후)도 보유로 보지 않음 → 시트가 checkin(죽은 달력) 대신
  // 재구매 오퍼를 렌더한다. 클럭 = 서버시간으로 통일(resolver 와 판정 불일치·기기 시계 skew 방지).
  if (BUtil.getCachedServerTime() - p.startAt >= (p.days + 1) * DAY_MS) return null;
  return p;
};

// 구매 가능 마감 = 노출 시작 + 3일. ⚠️ weeklypasspromo.js 의 WINDOW 와 항상 같은 값이어야 한다 —
// 어긋나면 진입점은 살아 있는데 시트가 만료로 판정해 구매 버튼이 잠긴다(2026-07-31 발생).
const OFFER_WINDOW_MS = 3 * DAY_MS;
const readOfferEndsAt = (target?: string): number | null => {
  const hist = (window as any).log?.weeklyPassOffer?.[target ?? ''];
  if (!hist || !hist.lastShownAt) return null;
  return hist.lastShownAt + OFFER_WINDOW_MS;
};

// 24시간 경계로 정밀도를 바꾼다 — 초가 3일 내내 도는 형태를 피한다. 체크인 타이머와 공유하는데
// 그쪽 인자는 항상 24시간 이하라 hh:mm:ss 가 유지된다.
const formatCountdown = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = String(Math.floor(total / 3600)).padStart(2, '0');
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return total > 24 * 3600 ? `${h}:${m}` : `${h}:${m}:${s}`;
};

const WeeklyPassOffer = () => {
  const { close, closeWithAnimation } = useBottomSheet();
  const { sheets } = useBottomSheet();
  const sheet = sheets.WeeklyPass;
  const { productId = 'wpass_500_50_7', target, reopen } = sheet.data ?? {};
  // 수치 홈 = productId 인코딩(wpass_<즉시>_<일일>_<일수>) — 서버 지급 파싱과 동일 원천
  const [, instantParsed, dailyParsed, daysParsed] = productId.split('_').map(Number);
  const instant = instantParsed || 500;
  const daily = dailyParsed || 50;
  const days = daysParsed || 7;

  // RTDB log 미러(window.log)가 유일한 상태 원천 — 1초 틱마다 재읽기로 구매/수령 반영
  // 초기 state 도 tombstone 을 거친다 — 재마운트 시 stale 미러로 죽은 화면이 뜨는 것을 막는다.
  const [pass, setPass] = useState<PassLog | null>(() => {
    const p = readPassLog();
    return p && revokedKeys.has(passKey(p)) ? null : p;
  });
  const [now, setNow] = useState(BUtil.getCachedServerTime());
  const [claiming, setClaiming] = useState(false);
  const coinPurchaseAnimationRef = useRef<CoinPurchaseAnimationRef>(null);
  // 미러 정리(RTDB remove)가 실패하면 stale 값이 계속 읽히고 1초 틱이 그걸 다시 집어와
  // 죽은 수령 화면을 복원한다 → tombstone 에 있는 미러는 없는 것으로 본다.
  const readLivePass = (): PassLog | null => {
    const p = readPassLog();
    if (p && revokedKeys.has(passKey(p))) return null;
    return p;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(BUtil.getCachedServerTime());
      setPass(readLivePass());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 닫기 확인 문구는 시트를 열 때 payload 로 함께 온다 — weeklypasspromo `closeConfirm`.
  // A·C 만 전용 문구(재오픈 안내)를 받고, B 는 registry 기본값(희소성 고지)을 그대로 쓴다.
  // 마운트 후 updateOptions 로 주입하면 시트가 다시 열릴 때 registry 기본값에 덮인다(실기기 실측).

  // 렌더 가드로 내용이 비면 시트 껍데기만 남는다 — 만료·회수 시점에 닫는다.
  // StrictMode 의 effect 재실행까지 포함해 이 마운트에서 자동 close 를 한 번만 수행한다.
  const closedRef = useRef(false);
  useEffect(() => {
    if (pass || target || closedRef.current) return;
    closedRef.current = true;
    close(sheet.id, { skipConfirm: true });
  }, [pass, target, close, sheet.id]);

  useEffect(() => {
    PurchaseManager.attach(function (err: any) {
      if (err) return;
      setPass(readLivePass());
    });
    return () => {
      PurchaseManager.detach();
    };
  }, []);

  const purchase = () => {
    PurchaseManager.buyCoin(productId);
    ba('weeklypass', undefined, { actionType: 'clicked-purchase', productId, fbUid: myinfo.fbUid, country: myinfo.country, device: Device.name });
  };

  const claim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const result = await WeeklyPassDao.claim();
      if (result.ok) {
        setPass(readLivePass());
        coinPurchaseAnimationRef.current?.show();
        // target·startAt = 구매 이벤트(weeklypass-purchase)와 조인해 오퍼별 리텐션 판독(권위 리텐션은 Mongo 원장).
        // checkin 재오픈 claim 은 target 미전달 → best-effort. startAt(=entitlement 키축)로 구매와 결합.
        ba('weeklypass', undefined, { actionType: 'claimed', slot: result.slot, fbUid: myinfo.fbUid, target, startAt: pass?.startAt ?? null });
      } else {
        // 실패를 무시하면 탭해도 아무 일이 없는 것처럼 보인다(= 미수령으로 오인하는 CS 경로).
        // 대부분의 실패는 서버 상태가 이미 바뀐 것(already-claimed·pass-expired·pass-revoked)이므로
        // 미러를 다시 읽어 시트가 실제 상태를 렌더하게 만든다.
        // ⚠️ 회수·만료는 **서버가 권위** — 미러 정리(RTDB remove)가 실패했으면 다시 읽어도 같은 stale 값이 와서
        //    수령 버튼이 계속 활성인 채 무한 실패한다. 이 두 경우는 그 패스를 tombstone 에 넣어
        //    1초 틱이 stale 미러로 화면을 복원하지 못하게 한다.
        if ((result.err === 'pass-revoked' || result.err === 'pass-expired') && pass) {
          revokedKeys.add(passKey(pass));
        }
        setPass(readLivePass());
        ba('weeklypass', undefined, { actionType: 'claim-failed', err: result.err, fbUid: myinfo.fbUid, target, startAt: pass?.startAt ?? null });
      }
    } finally {
      setClaiming(false);
    }
  };

  // 체크인으로 연 시트는 재구매 오퍼로 전환하지 않는다. 그 자리는 노출 게이트(`enabled`·자격)를
  // 지나지 않아, 꺼진 타겟 대역의 유저에게도 마감 없는 구매 버튼이 열린다(target 이 없어 유효창도 안 잡힌다).
  // 오퍼로 연 시트는 payload 에 target 이 있다 — weeklypasspromo `openOffer`.
  if (!pass && !target) return null;

  if (!pass) {
    const total = instant + daily * days;
    const details = PageShop.getProductDetails(productId);
    // 스토어 상품이 조회되지 않았으면 구매를 막는다. 하드코딩 폴백가로 버튼을 열어두면
    // 네이티브 상품 맵 미스로 **E002(Invalid product)** 가 나고 그 Activity 안에서는 복구 경로가 없다.
    // 일반 코인샵은 조회된 상품으로만 버튼을 만들어(pageShop `_initPage`) 이 문제가 없는데,
    // 이 시트만 E002 를 자기 몫으로 만들고 있었다(2026-08-03 실측 · 앱 전체 E002 의 95%).
    // ⚠️ 이 판정은 **네이티브 상품 맵의 권위 조회가 아니라 웹이 마지막으로 받은 목록의 프록시**다.
    //    `PageShop.products` 는 `_initPage` 에서 한 번만 채워지고 갱신 경로가 없다(pageShop.js:475).
    //    그래서 두 방향의 불일치가 남는다 — ① 웹엔 있는데 네이티브가 재조회로 그 SKU 를 잃으면 여전히 E002 다
    //    ② 웹이 RC 미완료 폴백 목록으로 초기화를 끝냈으면 네이티브에 상품이 있어도 **세션 내내 비활성**이다.
    //    후자는 1초 틱으로도 안 풀린다(웹 목록이 갱신되지 않으므로). 근본 해결은 네이티브 단건 재조회다.
    const productReady = !!(details && details.price);
    const price = productReady ? details.price : brsc.loading;
    const offerEndsAt = readOfferEndsAt(target);
    const offerRemainMs = offerEndsAt != null ? offerEndsAt - now : null;
    const offerExpired = offerRemainMs != null && offerRemainMs <= 0;
    // 구매 차단 사유는 둘(오퍼 만료·상품 미조회)이고 버튼 표현은 같다. 닫기 확인 생략은 만료에만 적용한다 —
    // 미조회는 곧 풀릴 수 있는 일시 상태라 재오픈 안내를 보여주는 편이 맞다.
    const purchaseDisabled = offerExpired || !productReady;
    // 진입점이 살아 있는 동안 계속 띄운다(홈 배지와 같은 규칙).
    const showCountdown = offerRemainMs != null && offerRemainMs > 0;

    return (
      <div className="flex h-full w-full flex-col items-center text-white">
        <div className="flex h-[56px] w-full shrink-0">
          {/* 오퍼 유효창이 끝나면(구매 버튼 비활성) 붙잡을 게 없으므로 확인 없이 닫는다 */}
          <Icon onClick={() => close(sheet.id, { skipConfirm: offerExpired })} name="icon/ic_close.svg" color="filter-base-ic-white" />
        </div>
        {/* 상단 시각 영역 — spotlight 위 제목/코인 겹침. flex-1 로 남는 세로 공간을 차지해 하단 흐름과 절대 겹치지 않는다.
            ⚠️ 위쪽은 다르다 — 62dvh 는 이 컨테이너보다 커서 items-end 로 바닥을 맞추면 초과분이 위로 넘쳐 닫기 행을 덮는다(의도된 연출).
            장식이라 pointer-events-none 이 필수다. 빼면 X 가 hit-test 에서 지고 시트를 닫을 방법이 사라진다 */}
        <div className="relative flex w-full min-h-0 flex-1 items-end justify-center">
          <SpotLightImage className="pointer-events-none mx-auto h-[62dvh] object-contain" />
          <div className="absolute bottom-0 left-[50%] w-full translate-x-[-50%] text-center">
            {reopen && <div className="typography-body-md text-[#FFC539]">{brsc.wpass_reopen}</div>}
            <div className="text-[40px] font-bold">{brsc.wpass_title}</div>
            <div className="text-[64px] font-bold leading-[110%] text-[#FFC539]">{brsc.wpass_total.replace('$param', total)}</div>
            <img className="z-1 mx-auto h-[22dvh] w-[22dvh] object-contain" alt="" src="/images/shop/coins/new_coin_500.png" loading="eager" />
          </div>
        </div>
        {/* 가격·타이머·안내 — 상단 시각 영역과 분리된 정상 흐름(세로 스택). 좁은 화면에서도 겹치지 않는다 */}
        <div className="flex w-full shrink-0 flex-col items-center gap-2 pt-2">
          <div className="typography-title-lg flex gap-2">
            <span className="typography-title-lg">{`${instant}${brsc.coins}`}</span>
            <span className="typography-title-lg text-[#FFC539]">+</span>
            <span className="typography-title-lg text-[#FFC539]">{brsc.wpass_drip.replace('$param1', daily).replace('$param2', days)}</span>
          </div>
          <div className="gr-box typography-body-md rounded-lg px-6 py-2">{price}</div>
        </div>
        <div className="w-full shrink-0 px-6 pt-2 pb-1 text-center">
          {offerExpired ? (
            <div className="typography-body-md text-white/60">{brsc.wpass_offer_expired}</div>
          ) : showCountdown ? (
            <div className="typography-body-md text-white/60">
              ⏰ <span className="text-[#FFC539]">{formatCountdown(offerRemainMs as number)}</span> {brsc.wpass_remain}
            </div>
          ) : null}
        </div>
        <div className="typography-body-md shrink-0 px-6 pb-2 text-center text-white/60">{brsc.wpass_notice}</div>
        <Button
          onClick={purchaseDisabled ? undefined : purchase}
          className="w-full shrink-0"
          style={{ text: brsc.purchase, background: purchaseDisabled ? '#555555' : '#FFC539', fontColor: 'black' }}
        />
        <CoinPurchaseAnimation ref={coinPurchaseAnimationRef} />
      </div>
    );
  }

  const passDays = pass.days ?? days;
  const passDaily = pass.daily ?? daily;
  const slotIndex = Math.floor((now - pass.startAt) / DAY_MS);
  const claimed = pass.claimed ?? {};
  const expired = slotIndex > passDays;
  const claimable = slotIndex >= 1 && slotIndex <= passDays && !claimed[`d${slotIndex}`];
  const nextAt = pass.startAt + (Math.max(slotIndex, 0) + 1) * DAY_MS;

  const slotState = (i: number) => {
    if (claimed[`d${i}`]) return 'claimed';
    if (i < slotIndex || expired) return 'missed';
    if (i === slotIndex) return 'today';
    return 'upcoming';
  };

  return (
    <div className="flex h-full w-full flex-col items-center text-white">
      <div className="flex h-[56px] w-full">
        <Icon onClick={() => closeWithAnimation(sheet.id)} name="icon/ic_close.svg" color="filter-base-ic-white" />
      </div>
      <div className="mt-[6dvh] text-center">
        <div className="text-[32px] font-bold">{brsc.wpass_title}</div>
      </div>
      <div className="mt-8 grid w-full grid-cols-7 gap-2 px-6">
        {Array.from({ length: passDays }, (_, idx) => {
          const i = idx + 1;
          const state = slotState(i);
          return (
            <div
              key={i}
              className={`flex flex-col items-center rounded-lg py-3 ${
                state === 'claimed'
                  ? 'bg-[#FFC539] text-black'
                  : state === 'today'
                    ? 'border border-[#FFC539] text-[#FFC539]'
                    : state === 'missed'
                      ? 'bg-white/5 text-white/30 line-through'
                      : 'bg-white/10 text-white/60'
              }`}
            >
              <span className="typography-body-md">{brsc.wpass_day.replace('$param', i)}</span>
              <span className="typography-title-md">{passDaily}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-auto w-full px-6 pb-2 text-center">
        {!claimable &&
          (slotIndex >= passDays ? (
            // 마지막 슬롯까지 수령 완료(또는 만료) — 더 받을 코인이 없다. 이때 nextAt 은 '다음 코인' 이 아니라
            // 패스 만료 시각이므로 카운트다운을 보여주면 오표기가 된다.
            <div className="typography-body-md text-white/60">{brsc.wpass_ended}</div>
          ) : (
            <div className="typography-body-md text-white/60">
              {/* slot 0 = 구매 직후(day1 미도래) — 아직 수령한 적이 없으므로 '받았어요' 는 slot>=1 에서만. 달력에 수령 표시가 없는데 문구만 받았다고 하는 모순 방지 */}
              {slotIndex >= 1 && `${brsc.wpass_claimed} · `}
              {brsc.wpass_next_in} <span className="text-[#FFC539]">{formatCountdown(nextAt - now)}</span>
            </div>
          ))}
      </div>
      <Button
        onClick={claimable && !claiming ? claim : undefined}
        className="w-full"
        style={{ text: brsc.wpass_claim.replace('$param', passDaily), background: claimable ? '#FFC539' : '#555555', fontColor: 'black' }}
      />
      <CoinPurchaseAnimation ref={coinPurchaseAnimationRef} />
    </div>
  );
};

export default WeeklyPassOffer;

const SpotLightImage = ({ className }: { className?: string }) => {
  return (
    <svg className={className} width="343" height="506" viewBox="0 0 343 506" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M64 0H279L343 470H0L64 0Z" fill="url(#paint0_linear_wpass)" />
      <ellipse cx="171.5" cy="470" rx="171.5" ry="36" fill="#886C26" />
      <defs>
        <linearGradient id="paint0_linear_wpass" x1="171.5" y1="0" x2="171.5" y2="470" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFC539" stopOpacity="0" />
          <stop offset="1" stopColor="#FFC539" stopOpacity="0.15" />
        </linearGradient>
      </defs>
    </svg>
  );
};
